<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const { variant, orientation, inReplyTo, shouldGroupWithNext, sender, isPrivate } =
  useMessageContext();
const { t } = useI18n();

// Nombre del agente que dejó la nota (Victor 2026-05-30): mostrar arriba
// del contenido cuando es una nota privada · ayuda a saber quién la escribió
// cuando varios agentes colaboran en el mismo chat.
const noteAuthorName = computed(() => {
  if (!isPrivate?.value) return null;
  return sender?.value?.name || null;
});

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  // Notas internas P3 (Victor 2026-05-28): gris neutro + franja dorada vertical
  // a la izquierda, estilo "callout" profesional. Se separa del verde WhatsApp
  // y del azul saliente · ver mockup notas-internas-izquierda-4-propuestas.html.
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-[#19191f] text-[#e7e7ea] border-l-[3px] border-l-[#c9a35a] !rounded-bl-none [&_.prosemirror-mention-node]:font-semibold [&_.prosemirror-mention-node]:text-[#c9a35a]',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});

// Autor del mensaje citado (Victor 2026-06-02): nombre real del remitente, o
// "Cliente"/"Tú" según sea entrante/saliente. Destaca que fue una respuesta.
const replyToAuthor = computed(() => {
  const m = inReplyTo?.value;
  if (!m) return '';
  if (m.sender?.name) return m.sender.name;
  const mt = m.messageType ?? m.message_type;
  return mt === 0 ? 'Cliente' : 'Tú';
});
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <!-- Cita de respuesta (Victor 2026-06-02): destaca que el mensaje responde a
         otro · al tocarla salta al mensaje original y lo resalta (glow dorado).
         Estilo aprobado en mockup saltar-a-mensaje-respondido-4-propuestas. -->
    <div
      v-if="inReplyTo"
      class="flex gap-2 items-stretch p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-[#c9a35a]/[0.08] border border-[#c9a35a]/20 hover:bg-[#c9a35a]/[0.14] transition-colors"
      title="Ir al mensaje respondido"
      @click="scrollToMessage"
    >
      <span class="w-[3px] rounded-full flex-none bg-[#c9a35a]" />
      <div class="min-w-0 flex-1">
        <div
          class="text-[11px] font-bold leading-tight"
          style="color: #c9a35a;"
        >
          ↩︎ En respuesta a — {{ replyToAuthor }}
        </div>
        <div
          v-dompurify-html="replyToPreview"
          class="prose prose-bubble line-clamp-1 text-xs opacity-70 mt-0.5"
        />
      </div>
      <span
        class="self-center text-sm font-bold opacity-50 flex-none"
        style="color: #c9a35a;"
        >↑</span
      >
    </div>
    <div
      v-if="noteAuthorName"
      class="text-[10px] font-semibold tracking-wider mb-1.5"
      style="color: #c9a35a;"
    >
      {{ noteAuthorName }}
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3 mt-2' : 'mt-0.5',
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-[#c9a35a]/70'
          : 'text-n-slate-11',
      ]"
    />
  </div>
</template>
